import { useEffect, useState } from "react";
import LazyImage from "@/my_components/Common/LazyImage";
import Link from "next/link";
import { useTranslation } from "react-i18next";

import { getFeaturedCollection } from "@/services/courseCollectionService";
import { ROUTE } from "@/route/app_routes.js";
import CourseHoverTooltip, { useCourseHoverTooltip } from "@/my_components/Common/CourseHoverTooltip";

const SKELETON_COUNT = 8;

/**
 * The curated "Featured Courses" list (course_collections, collection_type = 'featured') --
 * admin-curated and stats-auto-curated, capped at 8 (see the backend's CourseCollectionCurator
 * and CourseCollectionController). Previously just showed page 1 of the generic course listing
 * (getCourses({page:1, per_page:8})), which wasn't a curated list at all -- now reads the real
 * "featured" collection, same pattern as TrendingCourses.js/HeaderSearchBar.js.
 */
const FeaturedCourses = () => {
  const { t } = useTranslation("home");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { tooltip, show, hide, cancelHide } = useCourseHoverTooltip();

  useEffect(() => {
    getFeaturedCollection()
      .then((res) => setCourses(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rbt-course-grid">
        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <div className="rbt-course-card" key={i}>
            <div className="rbt-skel rbt-course-card__skel-media" />
            <div className="rbt-course-card__body">
              <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w60" style={{ height: 10 }} />
              <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w90" style={{ height: 14, marginTop: 4 }} />
              <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w60" style={{ height: 14 }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (courses.length === 0) return null;

  return (
    <div className="rbt-course-grid">
      {courses.map((course) => (
        <Link
          className="rbt-course-card"
          href={`${ROUTE.course_details}${course.id}`}
          key={course.id}
          onMouseEnter={(e) => show(course, e.currentTarget)}
          onMouseLeave={hide}
        >
          <div className="rbt-course-card__media">
            <LazyImage
              src={course.course_image_big}
              alt={course.title}
              fill
              sizes="(max-width: 480px) 92vw, (max-width: 767px) 45vw, (max-width: 1199px) 30vw, 22vw"
            />
            <div className="rbt-course-card__badges">
              {course.category && <span className="rbt-course-card__tag">{course.category}</span>}
              <span className="rbt-course-card__free">{t("featuredCourses.freeBadge", "Free")}</span>
            </div>
          </div>

          <div className="rbt-course-card__body">
            <div className="rbt-course-card__meta">
              <span><i className="feather-book"></i>{course.lessons} {t("featuredCourses.lessonsLabel", "lessons")}</span>
              <span><i className="feather-users"></i>{course.students}</span>
            </div>

            <h3 className="rbt-course-card__title">{course.title}</h3>

            <div className="rbt-course-card__foot">
              {course.skill_level ? (
                <span className="rbt-course-card__level">{course.skill_level}</span>
              ) : (
                <span />
              )}
              <span className="rbt-course-card__cta">
                {t("featuredCourses.viewCourse", "View course")} <i className="feather-arrow-right"></i>
              </span>
            </div>
          </div>
        </Link>
      ))}

      <CourseHoverTooltip tooltip={tooltip} onMouseEnter={cancelHide} onMouseLeave={hide} />
    </div>
  );
};

export default FeaturedCourses;
